import {useCallback, useEffect, useRef, useState} from "react";

/**
 * 区画別の再読み込みの「待ち秒数」— issue #2992 / 報告書 §8 No.4
 *
 * ■ なぜランダムを足すか
 *   混雑で失敗した利用者は同じタイミングで一斉に再読み込みする。全員に同じ秒数を出すと
 *   その秒数ちょうどに再び一斉突入し、上流が回復する前にまた飽和する。
 *   サーバー指示（ブレーカの遮断残り時間・既定 20 秒）に 10〜40 秒のランダムを足して散らす。
 *
 * ■ 2026-08-31 本番負荷テストの実測
 *   失敗した利用者はページ全体を再読み込みするため、1人あたり約 20 回
 *   （OTS 約 10 ＝ plan 3 ＋ stocks 3〜4 ＋ hotel 2〜3 ＋ 総数 1、フライト約 10）の
 *   上流コールが再発生していた。区画別＋待ち秒数で 3〜10 回まで減らす。
 */

/** ランダムに足す秒数の範囲 */
export const RETRY_JITTER_MIN_SEC = 10;
export const RETRY_JITTER_MAX_SEC = 40;

/**
 * 実際に待たせる秒数を決める。
 *
 * @param serverSec サーバーが返した retryAfterSec（未指定・負値は 0 扱い）
 * @returns serverSec + 10〜40 秒のランダム
 */
export const computeWaitSec = (serverSec?: number): number => {
  const base = Math.max(serverSec ?? 0, 0);
  const jitter =
    RETRY_JITTER_MIN_SEC +
    Math.floor(Math.random() * (RETRY_JITTER_MAX_SEC - RETRY_JITTER_MIN_SEC + 1));
  return base + jitter;
};

/**
 * 再読み込みボタン用のカウントダウン。
 *
 * `start(serverSec)` を呼ぶと待ち秒数を決めて 1 秒ずつ減らし、0 になったら `canRetry` が true になる。
 * 連打しても `start` を呼び直すまで秒数は再計算されない（＝待ち時間を短縮できない）。
 */
export const useRetryCountdown = () => {
  const [remainSec, setRemainSec] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, []);

  const start = useCallback((serverSec?: number) => {
    clear();
    const wait = computeWaitSec(serverSec);
    setRemainSec(wait);
    timerRef.current = setInterval(() => {
      setRemainSec((prev) => {
        if (prev <= 1) {
          clear();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clear]);

  const reset = useCallback(() => {
    clear();
    setRemainSec(0);
  }, [clear]);

  // アンマウント時にタイマーを止める（他画面へ遷移したあとに setState されるのを防ぐ）
  useEffect(() => clear, [clear]);

  return {remainSec, canRetry: remainSec <= 0, start, reset};
};
